// The following enables the 'css' prop. See: https://emotion.sh/docs/css-prop
/** @jsxImportSource @emotion/react */

import { FC, Suspense } from 'react';
import { CssBaseline, ThemeProvider, LinearProgress } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { indigo, pink } from '@mui/material/colors';
import * as Sentry from '@sentry/react';
import 'font-awesome/css/font-awesome.min.css';
import { useRecoilState } from 'recoil';
import { profileMenuOpenAtom } from 'packages/main/stores/global-recoil';
import { LoadingIndicator } from 'packages/main/components/Layouts/loading-indicator';
import { GIT_SHA_SHORT } from 'packages/env';
import { useTracking } from 'packages/lib/utils/hooks/use-tracking';
import { Link } from 'react-router-dom';
import { Button, Layout, Space, Typography } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { UserOutlined } from '@ant-design/icons';
import { Property } from 'csstype';
import MLSiteIcon from 'packages/lib/assets/icons/logo-aktifoB.svg?react';
import { mlSiteIconCSS } from 'packages/main/components/Layouts/main-nav';
import { Error500Result } from 'packages/lib/common-ui/error-pages/error-page-500';

const myCSS = {
  homeIcon: {
    fontSize: '26px',
    marginRight: '16px',
    marginTop: '4px',
    color: 'white',
    '&:hover': {
      color: 'lightgray',
    },
  },
  horizFlex: {
    display: 'flex',
    flexDirection: 'row' as Property.FlexDirection,
  },
};

const wrapperTheme = createTheme({
  palette: {
    primary: indigo,
    secondary: pink,
  },
  typography: {
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;`,
  },
});
export const drawerWidth = 240;
const useStyles = makeStyles()(() => ({
  main: {
    display: 'grid',
  },
  actionBar: { display: 'flex', alignItems: 'center' },
  header: {
    backgroundColor: '#001529 !important',
    '& .ant-space': { height: '100%' },
  },
  updateSW: {
    marginRight: '0.5rem',
    marginLeft: '0.5rem',
  },
}));

export const appBarTitleId = 'appbar-title';
export const appBarActionsId = 'appBarActions';
export const appBarTableActionsId = 'appBarTableActions';
export const appBarStickyHeaderId = 'appBarStickyHeader';
export const appBarMenuId = 'profle-menu-popover';
export const appBarMenuAnchorId = 'profile-menu-button';

export const PrivateLayoutExternal: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  useTracking();
  const { classes } = useStyles();
  const [menuOpen, setMenuOpen] = useRecoilState(profileMenuOpenAtom);

  const handleProfileMenuOpen = (
    event: React.MouseEvent<HTMLElement>
  ): void => {
    setMenuOpen(!menuOpen);
  };

  return (
    <ThemeProvider theme={wrapperTheme}>
      <Layout style={{ height: '100vh' }}>
        <CssBaseline />
        <Layout style={{ height: '100%' }}>
          <PageHeader
            ghost={true}
            title={
              <div css={myCSS.horizFlex}>
                <div>
                  <Link to="/contractor">
                    <MLSiteIcon
                      css={{
                        ...mlSiteIconCSS,
                        marginRight: '20px',
                      }}
                    />
                  </Link>
                </div>
                <span id={appBarTitleId} />
              </div>
            }
            style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
            className={classes.header}
            extra={[
              <Space
                className={classes.actionBar}
                key="actionBar"
                size="middle"
              >
                <span id={appBarActionsId} />
                <span id={appBarTableActionsId} />
                <Typography.Text style={{ color: 'white' }}>
                  {GIT_SHA_SHORT}
                </Typography.Text>
                <div
                  style={{
                    position: 'relative',
                  }}
                >
                  <Button
                    aria-label="account of current user"
                    data-testid="profile-menu-button"
                    type="primary"
                    icon={<UserOutlined />}
                    id={appBarMenuAnchorId}
                    aria-controls={appBarMenuId}
                    aria-haspopup="true"
                    size="large"
                    shape="circle"
                    onClick={handleProfileMenuOpen}
                  />
                  <LoadingIndicator />
                </div>
              </Space>,
              <div id={appBarStickyHeaderId} key="appBarStickyHeader" />,
            ]}
          />
          <Layout.Content
            className="site-layout-background"
            style={{ overflowY: 'auto' }}
          >
            <Sentry.ErrorBoundary
              beforeCapture={(scope): void => {
                scope.setTag('location', 'PrivateLayoutExternal root');
              }}
              fallback={<Error500Result />}
            >
              <Suspense fallback={<LinearProgress />}>{children}</Suspense>
            </Sentry.ErrorBoundary>
          </Layout.Content>
        </Layout>
      </Layout>
      <div id={appBarMenuId} data-testid="profile-menu-popover" />
    </ThemeProvider>
  );
};
export default PrivateLayoutExternal;
